import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

export type PromptSize = 'md' | 'lg' | 'xl' | 'default';
export type PromptOptions = SweetAlertOptions & { isDanger?: boolean };

/**
 * PromptingService - A centralized wrapper for SweetAlert2.
 * This service ensures consistent styling and provides a single point for alert logic.
 */
@Injectable({
  providedIn: 'root',
})
export class PromptingService {
  private readonly DEFAULT_CONFIRM_BUTTON_CLASS = 'btn btn-primary px-4';
  private readonly DEFAULT_CANCEL_BUTTON_CLASS = 'btn btn-light px-4';
  private readonly DEFAULT_DANGER_BUTTON_CLASS = 'btn btn-danger px-4';
  private readonly DEFAULT_WARNING_BUTTON_CLASS = 'btn btn-warning px-4';

  constructor() {}

  /**
   * Base fire method that wraps Swal.fire with standard defaults
   * @param options SweetAlertOptions
   * @param size Standard size ('md', 'lg', 'xl') applied via custom CSS class
   */
  async fire(
    options: PromptOptions,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    const { isDanger, ...swalNativeOptions } = options;

    let confirmButtonClass = this.DEFAULT_CONFIRM_BUTTON_CLASS;

    if (isDanger || options.icon === 'error') {
      confirmButtonClass = this.DEFAULT_DANGER_BUTTON_CLASS;
    } else if (options.icon === 'warning') {
      confirmButtonClass = this.DEFAULT_WARNING_BUTTON_CLASS;
    }

    const swalOptions: SweetAlertOptions = {
      buttonsStyling: false,
      heightAuto: false,
      ...swalNativeOptions,
      customClass: {
        popup: size === 'default' ? undefined : `swal-${size}`,
        confirmButton: confirmButtonClass,
        ...(swalNativeOptions.showCancelButton !== false
          ? { cancelButton: this.DEFAULT_CANCEL_BUTTON_CLASS }
          : {}),
        ...((options.customClass as any) || {}),
      },
    };

    return Swal.fire(swalOptions);
  }

  /**
   * Show a success alert
   */
  success(
    title: string,
    html?: string,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    return this.fire(
      {
        icon: 'success',
        title,
        html,
      },
      size,
    );
  }

  /**
   * Show an error alert
   */
  error(
    title: string,
    html?: string,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    return this.fire(
      {
        icon: 'error',
        title,
        html,
      },
      size,
    );
  }

  /**
   * Show a warning alert
   */
  warning(
    title: string,
    html?: string,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    return this.fire(
      {
        icon: 'warning',
        title,
        html,
      },
      size,
    );
  }

  /**
   * Show an info alert
   */
  info(
    title: string,
    html?: string,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    return this.fire(
      {
        icon: 'info',
        title,
        html,
      },
      size,
    );
  }

  /**
   * Show a confirmation dialog
   */
  confirm(
    options: PromptOptions,
    size: PromptSize = 'md',
  ): Promise<SweetAlertResult> {
    const standardConfirmOptions: PromptOptions = {
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      icon: 'warning',
      ...options,
    };

    return this.fire(standardConfirmOptions, size);
  }

  /**
   * Close the currently open alert
   */
  close(): void {
    Swal.close();
  }

  /**
   * Helper to get the input element (if any)
   */
  getInput(): HTMLInputElement | null {
    return Swal.getInput();
  }

  /**
   * Helper to get the confirm button element
   */
  getConfirmButton(): HTMLElement | null {
    return Swal.getConfirmButton();
  }

  /**
   * Helper to get the cancel button element
   */
  getCancelButton(): HTMLElement | null {
    return Swal.getCancelButton();
  }

  /**
   * Helper to get the icon element
   */
  getIcon(): HTMLElement | null {
    return Swal.getIcon();
  }
}
